import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import {
  User,
  CheckCircle,
  Loader2,
  Mail,
  Phone,
  FileText
} from 'lucide-react';
import { AdminMobileUserAPI, EnhancedMobileUser } from '../../lib/api';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface MobileUserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: EnhancedMobileUser;
}

export default function MobileUserEditModal({
  isOpen,
  onClose,
  onSuccess,
  user
}: MobileUserEditModalProps) {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    isActive: true,
    isPhoneVerified: false,
    adminNotes: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        isActive: user.isActive,
        isPhoneVerified: user.isPhoneVerified,
        adminNotes: user.adminNotes || ''
      });
    }
  }, [user]);

  // Update form data
  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await AdminMobileUserAPI.updateMobileUser(user._id, {
        firstName: formData.firstName,
        lastName: formData.lastName || undefined,
        email: formData.email || undefined,
        isActive: formData.isActive,
        isPhoneVerified: formData.isPhoneVerified,
        adminNotes: formData.adminNotes || undefined
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "User updated successfully",
          variant: "default"
        });

        handleClose();
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update user",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: "Error",
        description: "Failed to update user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="lg">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Edit Mobile User
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Update user information and account settings
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <EnhancedDialogBody>
            <FormSection
              title="Basic Information"
              icon={<User className="h-5 w-5" />}
              description="Update user's basic details"
            >
              <FormGrid columns={2}>
                <FormField
                  label="First Name"
                  required
                  error={errors.firstName}
                >
                  <Input
                    value={formData.firstName}
                    onChange={(e) => updateFormData('firstName', e.target.value)}
                    placeholder="Enter first name"
                  />
                </FormField>

                <FormField
                  label="Last Name"
                  hint="Optional"
                >
                  <Input
                    value={formData.lastName}
                    onChange={(e) => updateFormData('lastName', e.target.value)}
                    placeholder="Enter last name"
                  />
                </FormField>
              </FormGrid>

              <FormField
                label="Email Address"
                error={errors.email}
                hint="Optional - user's email address"
              >
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="Enter email address"
                    className="pl-10"
                  />
                </div>
              </FormField>

              <div className="text-sm text-muted-foreground bg-accent/50 p-3 rounded-md">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="h-4 w-4" />
                  <span className="font-medium">Phone Number (Read-only)</span>
                </div>
                <div className="font-mono">{user.phoneNumber || user.phone}</div>
                <div className="text-xs mt-1">Phone number cannot be changed from admin panel</div>
              </div>
            </FormSection>

            <FormSection
              title="Account Settings"
              icon={<CheckCircle className="h-5 w-5" />}
              description="Manage account status and verification"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Account Status"
                  hint="Enable or disable user account"
                >
                  <Select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onValueChange={(value) => updateFormData('isActive', value === 'active')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  label="Phone Verification"
                  hint="Mark phone as verified/unverified"
                >
                  <Select
                    value={formData.isPhoneVerified ? 'verified' : 'unverified'}
                    onValueChange={(value) => updateFormData('isPhoneVerified', value === 'verified')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select verification status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="unverified">Unverified</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title="Admin Notes"
              icon={<FileText className="h-5 w-5" />}
              description="Internal notes about this user"
            >
              <FormField
                label="Notes"
                hint="Internal notes visible only to admins"
              >
                <Textarea
                  value={formData.adminNotes}
                  onChange={(e) => updateFormData('adminNotes', e.target.value)}
                  placeholder="Enter admin notes..."
                  rows={3}
                />
              </FormField>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Update User
                </>
              )}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}